if (!customElements.get('color-swatches')) {
  customElements.define(
    'color-swatches',
    class colorSwatches extends HTMLElement {
      constructor () {
        super();
        this.params = {
          selectors: {
            root: '.product-card-wrapper',
            images: '.advanced-media > img',
            price: '.card__information-primary > .price',
            badge: '.advanced-product-badge[data-dynamic-product-badge="true"]',
            badgeData: 'script[data-dynamic-product-badge-data]',
            replaceablePromoBadge: '[data-promo-badge-replaceable="true"]',
          },
        };

        this.swatches = this.querySelectorAll('*[data-type="swatch"]');
        this.root = this.closest(this.params.selectors.root);
        this.images = [];
        this.initialImages = [];
        const variantID = this.querySelector('*[data-type="swatch"].active')?.getAttribute('data-variant-id');
        this.initialVariantID = variantID ? variantID * 1 : null;
        this.badgeData = null;
        this.restrictByVariants = this.hasAttribute('data-allowed-variant-ids');
        this.allowedVariantIds = null;
        const allowedVariantIdsData = this.restrictByVariants ? this.getAttribute('data-allowed-variant-ids') : null;
        if (this.restrictByVariants && allowedVariantIdsData) {
          try {
            const parsedAllowedIds = JSON.parse(allowedVariantIdsData);
            if (Array.isArray(parsedAllowedIds)) {
              this.allowedVariantIds = new Set(parsedAllowedIds.map((id) => Number(id)));
            }
          } catch (e) {
            console.error(e.message);
          }
        }
        // Duplicate cards of the same product (variant-split grids) may skip
        // their own JSON payload and share the blob of the first card instead.
        let data = this.querySelector(`*[data-type="product"]`)?.textContent;
        if (!data) {
          const productId = this.getAttribute('data-product-id');
          if (productId) {
            data = document.querySelector(`script[data-type="product"][data-product-id="${productId}"]`)?.textContent;
          }
        }
        this.product = null;
        try {
          if (data) {
            this.product = JSON.parse(data);
          }
        } catch (e) {
          console.error(e.message);
        }
      }

      isVariantAllowed (variantID) {
        if (!this.restrictByVariants || !this.allowedVariantIds || this.allowedVariantIds.size === 0) {
          return true;
        }
        return this.allowedVariantIds.has(Number(variantID));
      }

      getSecondImage (variant) {
        let featured = variant?.featured_image?.src ?? null;
        let result;
        let found = false;
        for (let image of this.product.images) {
          if (image === featured) {
            found = true;
            continue;
          }
          if (found) {
            result = image;
            break;
          }
        }
        return (result) ? result : featured;
      }

      findVariantByColor (variantIDs, color) {
        for (const variant of this.product.variants) {
          if (!variantIDs.includes(variant.id) && this.isVariantAllowed(variant.id) && variant.title.indexOf(color) !== -1 && variant.featured_image?.src) {
            return variant;
          }
        }
        return null;
      }

      imageProcessing (variantData, color) {
        let images = [];
        if (variantData.id === this.initialVariantID) {
          images = this.initialImages;
        } else {
          if (variantData) {
            let mainImage = variantData.featured_image?.src ?? null;
            let secondaryImage = null;
            const excludedVariants = [variantData.id];
            if (!mainImage) {
              const primaryVariant = this.findVariantByColor(excludedVariants, color);
              if (primaryVariant) {
                mainImage = primaryVariant ? primaryVariant.featured_image.src : null;
                excludedVariants.push(primaryVariant.id);
              }
            }
            if (mainImage) {
              images.push(mainImage);
            }
            const secondaryVariant = this.findVariantByColor(excludedVariants, color);
            if (secondaryVariant) {
              secondaryImage = this.getSecondImage(secondaryVariant);
            }
            if (secondaryImage) {
              images.push(secondaryImage);
            } else if (mainImage && this.images.length > 1) {
              images.push(mainImage);
            }
          }
        }

        if (images.length) {
          this.images.forEach((item, index) => {
            if (images[index]) {
              item.onload = () => {
                item.removeAttribute('srcset');
                item.removeAttribute('sizes');
              };
              item.src = images[index];
            }
          });
        }
      }

      setStatus (variantID) {
        this.swatches.forEach((item) => {
          let currentVariantID = item.getAttribute('data-variant-id');
          if (currentVariantID) {
            currentVariantID = currentVariantID * 1;
          }
          if (currentVariantID === variantID) {
            item.classList.add('active');
          } else {
            item.classList.remove('active');
          }
        });
      }

      priceProcessing (initiator, variantData) {
        const priceContainer = this.root.querySelector(this.params.selectors.price);
        const initiatorPrice = initiator.getAttribute('data-price');
        const initiatorCompareAtPrice = initiator.getAttribute('data-compare-at-price');
        if (priceContainer && !priceContainer.classList.contains('volume-pricing--sale-badge') && initiatorPrice) {
          const regularContainer = priceContainer.querySelector('.price__regular > .price-item.price-item--regular');
          if (regularContainer) {
            regularContainer.innerHTML = initiatorPrice;
          }
          const priceSaleContainer = priceContainer.querySelector('.price__sale > .price-item.price-item--sale');
          if (priceSaleContainer) {
            priceSaleContainer.innerHTML = initiatorPrice;
          }
          const priceCompareContainer = priceContainer.querySelector('.price__sale .price-item.price-item--regular');
          if (variantData.compare_at_price > variantData.price && initiatorCompareAtPrice) {
            priceCompareContainer.innerHTML = initiatorCompareAtPrice;
            priceContainer.classList.add('price--on-sale');
          } else {
            priceCompareContainer.innerHTML = '';
            priceContainer.classList.remove('price--on-sale');
          }
        }
      }

      getBadgeData () {
        if (this.badgeData !== null) {
          return this.badgeData;
        }

        this.badgeData = { badges: [], variants: {} };
        const rawData = this.root.querySelector(this.params.selectors.badgeData)?.textContent;
        if (!rawData) {
          return this.badgeData;
        }

        try {
          const parsedData = JSON.parse(rawData);
          if (parsedData && Array.isArray(parsedData.badges) && parsedData.variants) {
            this.badgeData = parsedData;
          }
        } catch (e) {
          console.error(e.message);
        }

        return this.badgeData;
      }

      matchBadge (variantID) {
        if (variantID == null) {
          return null;
        }

        const data = this.getBadgeData();
        const badgeIndex = data.variants[String(variantID)];
        if (!Number.isInteger(badgeIndex)) {
          return null;
        }

        return data.badges[badgeIndex] || null;
      }

      setBadgeColorProperty (badge, property, value) {
        if (value) {
          badge.style.setProperty(property, value);
        } else {
          badge.style.removeProperty(property);
        }
      }

      togglePromoBadgeReplacement (isDynamicBadgeVisible) {
        const promoBadge = this.root.querySelector(this.params.selectors.replaceablePromoBadge);
        if (!promoBadge) {
          return;
        }

        if (isDynamicBadgeVisible) {
          promoBadge.setAttribute('data-promo-badge-hidden-by-dynamic', 'true');
        } else {
          promoBadge.removeAttribute('data-promo-badge-hidden-by-dynamic');
        }
      }

      badgeProcessing (variantID) {
        const badge = this.root.querySelector(this.params.selectors.badge);
        if (!badge) {
          return;
        }

        const matchedRule = this.matchBadge(variantID);

        if (!matchedRule) {
          badge.textContent = '';
          badge.setAttribute('data-dynamic-product-badge-empty', 'true');
          this.setBadgeColorProperty(badge, '--color-badge-background');
          this.setBadgeColorProperty(badge, '--color-badge-foreground');
          this.setBadgeColorProperty(badge, '--badge-border-color');
          this.togglePromoBadgeReplacement(false);
          return;
        }

        badge.textContent = matchedRule.text;
        badge.removeAttribute('data-dynamic-product-badge-empty');
        this.setBadgeColorProperty(badge, '--color-badge-background', matchedRule.background);
        this.setBadgeColorProperty(badge, '--color-badge-foreground', matchedRule.color);
        this.setBadgeColorProperty(badge, '--badge-border-color', matchedRule.borderColor);
        this.togglePromoBadgeReplacement(true);
      }

      linkProcessing (variantID) {
        this.root.querySelectorAll('a[id^="CardLink-template"],a[id^="StandardCardNoMediaLink"]').forEach((link) => {
          link.href = this.updateVariantId(link.href, variantID);
        });

        this.root.querySelectorAll('.advanced-quick-view').forEach((link) => {
          const href = link.getAttribute('data-href');
          link.setAttribute('data-href', this.updateVariantId(href, variantID));
        });
      }

      clickHandle (e) {
        e.preventDefault();
        e.stopPropagation();
        const initiator = e.target.closest('*[data-type="swatch"]');
        let processed = false;
        const isActive = initiator.classList.contains('active');
        if (initiator && !isActive) {
          let variantID = initiator.getAttribute('data-variant-id');
          const color = initiator.getAttribute('data-color');
          if (variantID && color) {
            variantID = variantID * 1;
            const variantData = this.product.variants.find((variant) => variantID === variant.id);
            if (variantData && this.isVariantAllowed(variantData.id)) {
              this.linkProcessing(variantID);
              this.imageProcessing(variantData, color);
              this.priceProcessing(initiator, variantData);
              this.badgeProcessing(variantID);
              this.setStatus(variantID);
              processed = true;
            }
          }
        }
        if (!processed && initiator.hasAttribute('href') && !isActive) {
          window.location.href = initiator.getAttribute('href');
        }
      }

      updateVariantId (href, variantId) {
        const url = new URL(href, window.location.origin);
        if (variantId) {
          url.searchParams.set('variant', variantId);
        }
        return url.pathname + url.search;
      }

      connectedCallback () {
        if (this.product && this.root) {
          this.images = this.root.querySelectorAll(this.params.selectors.images);
          this.images.forEach((item) => {
            this.initialImages.push(item.src);
          });

          this.linkProcessing(this.initialVariantID);
          this.swatches.forEach((item) => {
            item.addEventListener('click', this.clickHandle.bind(this));
          });
        }
      }

      disconnectedCallback () {
        if (this.product && this.root) {
          this.images = [];
          this.swatches.forEach((item) => {
            item.removeEventListener('click', this.clickHandle.bind(this));
          });
        }
      }
    },
  );
}
